import { Theme, useTheme } from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_ColumnFilterFnsState,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
  MRT_TableInstance,
  useMaterialReactTable,
} from "material-react-table";
import React, { ReactNode, useState } from "react";

import { useMuiTableToolbarAlertBannerProps } from "@/hooks/use-mui-table-toolbar-alert-banner-props";
import { useTableSettings } from "@/hooks/use-table-settings";
import { SysAccountRegularDto, SysCfgRegularDto } from "@/shared/api";

export const useAccountsTable = ({
  columns,
  data,
  isDataLoading,
  isDataRefetching,
  isDataLoadingError,
  loadingErrorMessage,
  totalElements,
  totalPages,
  pagination,
  setPagination,
  columnFilters,
  setColumnFilters,
  columnFilterFns,
  setColumnFilterFns,
  sorting,
  setSorting,
  renderToolbarCustomActions,
  columnVisibility,
  name,
  enableColumnDragging = false,
  renderBottomToolbarCustomActions,
}: {
  columns: MRT_ColumnDef<SysAccountRegularDto>[];
  data?: SysAccountRegularDto[];
  isDataLoading?: boolean;
  isDataRefetching?: boolean;
  isDataLoadingError?: boolean;
  loadingErrorMessage?: string;
  totalElements?: number;
  totalPages?: number;
  pagination: MRT_PaginationState;
  setPagination: React.Dispatch<React.SetStateAction<MRT_PaginationState>>;
  columnFilters: MRT_ColumnFiltersState;
  setColumnFilters: React.Dispatch<React.SetStateAction<MRT_ColumnFiltersState>>;
  columnFilterFns: MRT_ColumnFilterFnsState;
  setColumnFilterFns: React.Dispatch<React.SetStateAction<MRT_ColumnFilterFnsState>>;
  sorting: MRT_SortingState;
  setSorting: React.Dispatch<React.SetStateAction<MRT_SortingState>>;
  renderToolbarCustomActions?: (props: {
    table: MRT_TableInstance<SysAccountRegularDto>;
  }) => ReactNode;
  renderBottomToolbarCustomActions?: (props: {
    table: MRT_TableInstance<SysAccountRegularDto>;
  }) => ReactNode;
  columnVisibility?: { [key: string]: boolean };
  name: string;
  enableColumnDragging?: boolean;
}) => {
  useTheme();
  const { defaultProps, state } = useTableSettings<SysCfgRegularDto>(name);
  const muiToolbarAlertBannerProps = useMuiTableToolbarAlertBannerProps(
    Boolean(isDataLoadingError),
    loadingErrorMessage
  );

  /*const [shouldPinName, setShouldPinName] = useState(false);*/

  return useMaterialReactTable({
    data: data ?? [],
    columns: columns,
    getRowId: (originalRow) => originalRow.name,
    ...defaultProps,
    initialState: {
      columnVisibility: {
        "data.clubs": false,
        "data.alc": false,
        "data.gold": false,
        ...columnVisibility,
      },
      density: "compact",
      //columnPinning: shouldPinName ? { left: ["mrt-row-select", 'name'] } : {},
      columnPinning: { left: ["mrt-row-select", "enabled", "name"] },
    },
    selectAllMode: "page",
    enableSelectAll: true,
    displayColumnDefOptions: {
      "mrt-row-select": { header: "" },
    },
    enableRowSelection: true,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enableColumnFilterModes: true,
    muiToolbarAlertBannerProps,

    enablePagination: true,
    manualPagination: true,
    rowCount: totalElements ?? 0,
    pageCount: totalPages ?? 0,

    enableStickyHeader: true,
    enableSorting: true,
    autoResetPageIndex: false,
    enableGlobalFilter: false,
    state: {
      ...state,
      pagination: pagination,
      columnFilters: columnFilters,
      columnFilterFns: columnFilterFns,
      sorting: sorting,
      isLoading: isDataLoading,
      showProgressBars: isDataRefetching,
    },
    defaultColumn: {
      minSize: 20, //allow columns to get smaller than default
      maxSize: 40, //allow columns to get larger than default
      size: 40, //make columns wider by default
    },
    /*muiTableContainerProps: {
      onScroll: (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
        setShouldPinName(e.currentTarget.scrollLeft > 250);
      },
    },*/
    muiTableBodyRowProps: ({ row }) => {
      const lightThemeGray = "#f0eded";
      const darkThemeRed = "#fa4343";
      const darkThemeGray = "#424242";

      const isAccountStatusOk = (row.original.data as any)?.status === 0;
      const isAccountEnabled = row.original.enabled === true;

      let sx = {};

      if (!isDataLoading) {
        if (!isAccountStatusOk) {
          sx = {
            ...sx,
            borderBottom: `2px solid ${darkThemeRed}`, // оранжевая граница
            "& .MuiTableCell-root": {
              borderBottom: "inherit", // наследуем цвет от строки
            },
          };
        }
        if (!isAccountEnabled) {
          sx = {
            ...sx,
            backgroundColor: (theme: Theme) =>
              theme.palette.mode === "dark" ? darkThemeGray : lightThemeGray,
          };
        }
        return { sx: { ...sx } };
      } else return { sx: { ...sx } };
    },
    onPaginationChange: (value) => {
      setPagination(value);
    },
    onColumnFiltersChange: setColumnFilters,
    onColumnFilterFnsChange: setColumnFilterFns,
    onSortingChange: setSorting,
    renderTopToolbarCustomActions: renderToolbarCustomActions,
    //renderBottomToolbarCustomActions: renderBottomToolbarCustomActions,
  });
};
